/**
 * Demo Storage Service
 * Manages localStorage for demo organization entries
 */

const DEMO_STORAGE_PREFIX = 'demo_org_'
const SESSION_EXPIRY_HOURS = 24 // Session expires after 24 hours

/**
 * Get storage key for demo entries
 */
const getStorageKey = (type) => {
  return `${DEMO_STORAGE_PREFIX}${type}`
}

/**
 * Get session expiry timestamp
 */
const getSessionExpiry = () => {
  return Date.now() + SESSION_EXPIRY_HOURS * 60 * 60 * 1000
}

/**
 * Check if session is expired
 */
const isSessionExpired = () => {
  const expiry = localStorage.getItem(`${DEMO_STORAGE_PREFIX}session_expiry`)
  if (!expiry) return true
  return Date.now() > parseInt(expiry, 10)
}

/**
 * Initialize or refresh session
 */
export const initDemoSession = () => {
  localStorage.setItem(`${DEMO_STORAGE_PREFIX}session_expiry`, getSessionExpiry().toString())
}

/**
 * Clear expired demo data
 */
export const clearExpiredDemoData = () => {
  if (isSessionExpired()) {
    // Clear all demo entries
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith(DEMO_STORAGE_PREFIX)) {
        localStorage.removeItem(key)
      }
    })
    return true
  }
  return false
}

/**
 * Get demo entries for a type
 */
export const getDemoEntries = (type) => {
  if (isSessionExpired()) {
    clearExpiredDemoData()
    return []
  }

  const key = getStorageKey(type)
  const data = localStorage.getItem(key)
  if (!data) return []
  
  try {
    return JSON.parse(data)
  } catch (error) {
    console.error('Error parsing demo entries:', error)
    return []
  }
}

/**
 * Save demo entry
 */
export const saveDemoEntry = (type, entry) => {
  if (isSessionExpired()) {
    initDemoSession()
  }

  const entries = getDemoEntries(type)
  const newEntry = {
    ...entry,
    id: entry.id || `demo-${type}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    created_at: entry.created_at || new Date().toISOString(),
  }
  
  entries.push(newEntry)
  
  const key = getStorageKey(type)
  localStorage.setItem(key, JSON.stringify(entries))
  
  return newEntry
}

/**
 * Check if demo user has reached entry limit
 */
export const hasReachedDemoLimit = (type) => {
  const entries = getDemoEntries(type)
  return entries.length >= 1 // Limit to 1 entry per type
}

/**
 * Get demo entry count
 */
export const getDemoEntryCount = (type) => {
  return getDemoEntries(type).length
}

/**
 * Clear all demo data
 */
export const clearAllDemoData = () => {
  Object.keys(localStorage).forEach((key) => {
    if (key.startsWith(DEMO_STORAGE_PREFIX)) {
      localStorage.removeItem(key)
    }
  })
}

